/**
 * Tiny helpers over the PixiJS v8 Graphics API so the object builders read like
 * pixel-stamping rather than path bookkeeping. Everything draws at integer
 * virtual-pixel coordinates; crispness comes from the renderer running with
 * `antialias: false` at 480×270 and the canvas being upscaled with
 * `image-rendering: pixelated`.
 */
import { Graphics } from 'pixi.js'

/** Filled axis-aligned block. */
export function rect(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  alpha = 1,
): Graphics {
  g.rect(x, y, w, h).fill({ color, alpha })
  return g
}

/** 1px-thick stroked outline, drawn *inside* the given box. */
export function outline(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  color: number,
  width = 1,
  alpha = 1,
): Graphics {
  g.rect(x, y, w, h).stroke({ width, color, alpha, alignment: 1 })
  return g
}

/** Filled triangle / polygon from a flat [x0,y0,x1,y1,…] list. */
export function poly(g: Graphics, points: number[], color: number, alpha = 1): Graphics {
  g.poly(points).fill({ color, alpha })
  return g
}

/** Filled circle. */
export function circle(
  g: Graphics,
  cx: number,
  cy: number,
  r: number,
  color: number,
  alpha = 1,
): Graphics {
  g.circle(cx, cy, r).fill({ color, alpha })
  return g
}

/**
 * Stacked horizontal colour bands across [x..x+w], top→bottom. Each colour in
 * `colors` gets an equal-height slice — a chunky stand-in for a gradient that
 * keeps the limited-palette pixel-art feel.
 */
export function bands(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  colors: readonly number[],
): Graphics {
  const step = h / colors.length
  for (let i = 0; i < colors.length; i++) {
    const y0 = Math.round(y + i * step)
    const y1 = Math.round(y + (i + 1) * step)
    rect(g, x, y0, w, y1 - y0, colors[i])
  }
  return g
}
